import { faUserTie } from '@fortawesome/pro-regular-svg-icons'
import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Column, SortingRule } from 'react-table'
import { useInView } from 'react-intersection-observer'
import flatten from 'lodash/flatten'
import isEqual from 'lodash/isEqual'

import { Customer } from '../types/customer'
import { useParamsToObj } from '../hooks/useParamsToObj'
import { useCustomers } from '../hooks/useCustomers'
import { useUser } from '../hooks/useUser'

import EmptyContent from '../components/EmptyContent'
import ErrorPage from '../components/ErrorPage'
import PageHeader from '../components/PageHeader'
import Table from '../components/Table'
import TableSkeleton from '../components/TableSkeleton'
import Spinner from '../components/Spinner'

const Customers: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const urlParams = useParamsToObj(['search'])
  const [orderByFilter, setOrderBy] = useState<SortingRule<Customer>[]>([])
  const customers = useCustomers(urlParams, orderByFilter)
  const user = useUser()

  const { ref, inView } = useInView({
    rootMargin: '100px 0px 0px 0px',
  })
  const { hasNextPage, fetchNextPage } = customers

  useEffect(() => {
    const init = async () => {
      if (inView && hasNextPage) {
        await fetchNextPage()
      }
    }
    init()
  }, [inView, hasNextPage, fetchNextPage])

  // Handle react-table sorting changes (in Table.tsx)
  const handleSortByChange = useCallback(
    (orderBy: SortingRule<any>[]) => {
      if (isEqual(orderByFilter, orderBy)) return

      setOrderBy(orderBy)
    },
    [orderByFilter]
  )

  const customersData = flatten(customers.data?.pages)

  const columns = React.useMemo<Column<Customer>[]>(
    () => [
      {
        id: 'name',
        Header: 'Customer',
        accessor: 'name',
      },
      {
        id: 'nbClaimedPlaces',
        Header: 'locations',
        accessor: 'nbClaimedPlaces',
      },
      {
        id: 'nbRemainingLicenses',
        Header: 'License Remaining',
        accessor: 'nbRemainingLicenses',
      },
      {
        id: 'nbExperiences',
        Header: '# Of Experiences',
        accessor: 'nbExperiences',
      },
      {
        id: 'createdAt',
        Header: 'Date Joined',
        accessor: ({ createdAt }) => new Date(createdAt).toLocaleDateString('en-GB'),
      },
      {
        Header: ' ',
        disableSortBy: true,
        accessor: ({ id }) => (
          <button
            onClick={() => {
              navigate(`/customers/${id}`)
            }}
            className="text-link"
          >
            View
          </button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  if (customers.isLoading)
    return (
      <div>
        <PageHeader
          title="Customers"
          btnAction={() => navigate('/customers/add-new')}
          btnText={user.data?.type === 'BUZZL' && user.data?.role === 'ADMIN' ? 'Add Customer' : undefined}
          urlParams={urlParams}
          searchPlaceholder="Search Customer Name"
        />
        <TableSkeleton
          headers={['Customer', 'locations', 'License Remaining', '# Of Experiences', 'Date Joined', ' ']}
        />
      </div>
    )

  if (customers.isError || !customers) {
    return (
      <ErrorPage
        title="Something went wrong!"
        text="We keep track of these errors, but feel free to contact us if refreshing doesn't fix things."
      />
    )
  }

  if (customersData.length === 0 && !location.search) {
    return (
      <div className="mt-32 flex h-full items-center justify-center">
        <EmptyContent
          title="No customers found."
          description="Start by adding a new one."
          icon={faUserTie}
          btnAction={() => navigate('/customers/add-new')}
          btnText={user.data?.type === 'BUZZL' && user.data?.role === 'ADMIN' ? 'Add Customer' : undefined}
        />
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Customers"
        btnAction={() => navigate('/customers/add-new')}
        btnText={user.data?.type === 'BUZZL' && user.data?.role === 'ADMIN' ? 'Add Customer' : undefined}
        urlParams={urlParams}
        searchPlaceholder="Search Customer Name"
      />
      <Table columns={columns} data={customersData} onSortBy={handleSortByChange} />
      {hasNextPage ? (
        <div ref={ref} className="mt-4 mb-4 flex flex-col items-center text-center">
          <Spinner className="h-8 w-8 text-gray-400" />
          <span className="text-gray-400">Loading more...</span>
        </div>
      ) : null}
    </div>
  )
}

export default Customers
